// @flow

import React, { useCallback, useMemo } from "react";
import { connect, useSelector } from "react-redux";

import { compose, flatten, isEmpty, prop } from "ramda";

import { Translate, useI18n } from "@quantum/i18n";
import { RequiresPermission } from "@quantum/permission";

import {
  type ErrosSelecao,
  type Id,
  type MedidaSelecionada,
  type MedidaUsuarioTipo,
  MODO_APP_NORMAL,
  MODO_APP_SERIES,
} from "../../../../model";

import {
  type ActionCreator,
  type State,
  i18n,
  abrirModalMedidaUsuario,
  selecionarMedidaUsuario,
  getIdMedidaUsuarioSelecionada,
  getMedidasUsuario,
  getModoApp,
} from "../../../../state";

import SelectInput, { useSelectState } from "../../../comum/SelectInput";

import "./MedidaUsuario.css";

export const criaFuncaoValidacaoMedidaUsuario = (
  state: State
) => (): ?ErrosSelecao => {
  const erros: ErrosSelecao = {};
  const { translate } = i18n(state);

  if (isEmpty(getIdMedidaUsuarioSelecionada(state))) {
    erros.ativoNaoSelecionado = translate(
      "configuracaoRelatorio.selecioneUmaMedida"
    );
  }

  return erros;
};

export const processarMedidaUsuario = (
  state: State,
  medidas: Array<MedidaSelecionada>
): Array<MedidaSelecionada> => {
  const medidasUsuarioSelecionadas = getIdMedidaUsuarioSelecionada(state);

  return medidasUsuarioSelecionadas.flatMap(idMedidaUsuario =>
    medidas.map(medidasSelecionada => ({
      ...medidasSelecionada,
      idMedidaUsuario,
    }))
  );
};

type Prop = {
  medidasUsuario: Array<MedidaUsuarioTipo>,
  medidaUsuarioSelecionada: Id | Array<Id>,
  abrirModalMedidaUsuario: ActionCreator,
  selecionarMedidaUsuario: (Id | Array<Id>) => mixed,
  desmarcarMedidas: ActionCreator,
};

const MedidaUsuario = ({
  medidasUsuario,
  medidaUsuarioSelecionada,
  abrirModalMedidaUsuario,
  selecionarMedidaUsuario,
  desmarcarMedidas,
}: Prop) => {
  const { translate } = useI18n();
  const modoApp = useSelector(getModoApp);
  const selectState = useSelectState("medidaUsuarioSelect");

  const medidasSelecionadas: Array<Id> = useMemo(
    () => flatten([medidaUsuarioSelecionada]).filter(Boolean),
    [medidaUsuarioSelecionada]
  );

  const aoAlterarMedidaUsuario = useCallback(
    medidas => {
      selecionarMedidaUsuario(
        medidasUsuario
          .filter(medida => medidas.includes(medida.id))
          .map(prop("id"))
      );
    },
    [medidasUsuario, selecionarMedidaUsuario]
  );

  return (
    <div className="ConfiguracaoMedida--no-flex">
      <div className="ConfiguracaoMedida--row">
        <div className="ConfiguracaoMedida">
          <div className="MedidaUsuario__label">
            <Translate>Comum.MinhasMedidas</Translate>
            <div className="MedidaUsuario__label-wrapper">
              {medidasSelecionadas.length >= 1 && (
                <span
                  className="MedidaUsuario__label-wrapper--desmarcar"
                  onClick={desmarcarMedidas}
                >
                  <Translate>Comum.desmarcarTodos</Translate>
                </span>
              )}

              {(modoApp === MODO_APP_NORMAL || modoApp === MODO_APP_SERIES) && (
                <RequiresPermission permission="MEDIDAS_USUARIO_EDICAO">
                  <span
                    className="MedidaUsuario__label-wrapper--importar"
                    onClick={abrirModalMedidaUsuario}
                  >
                    <Translate>Comum.BotaoImportarMedidas</Translate>
                  </span>
                </RequiresPermission>
              )}
            </div>
          </div>

          <SelectInput
            {...selectState}
            key={"medida-usuario"}
            disabled={medidasUsuario.length === 0}
            className="MedidaUsuario__select"
            value={medidasSelecionadas}
            options={medidasUsuario}
            campoId="id"
            campoDescricao="descricao"
            placeholder={translate("maiusculas.selecioneUmaMedida")}
            multiple
            onChange={aoAlterarMedidaUsuario}
          />
        </div>
      </div>
    </div>
  );
};

export default compose(
  connect(
    (state: State) => ({
      medidasUsuario: getMedidasUsuario(state),
      medidaUsuarioSelecionada: getIdMedidaUsuarioSelecionada(state),
    }),
    { abrirModalMedidaUsuario, selecionarMedidaUsuario },
    (state, { abrirModalMedidaUsuario, selecionarMedidaUsuario }) => ({
      ...state,
      abrirModalMedidaUsuario,
      selecionarMedidaUsuario,
      desmarcarMedidas: () => selecionarMedidaUsuario([]),
    })
  )
)(MedidaUsuario);
